import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiService from '../services/apiService';

const useSettingsStore = create(
  persist(
    (set, get) => ({
      // Default settings
      currency: 'USD',
      currencySymbol: '$',
      currencyLocale: 'en-US',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      
      // Currency configuration mapping
      currencyConfig: {
        USD: { symbol: '$', locale: 'en-US', name: 'US Dollar' },
        EUR: { symbol: '€', locale: 'de-DE', name: 'Euro' },
        GBP: { symbol: '£', locale: 'en-GB', name: 'British Pound' },
        INR: { symbol: '₹', locale: 'en-IN', name: 'Indian Rupee' },
        JPY: { symbol: '¥', locale: 'ja-JP', name: 'Japanese Yen' },
        CAD: { symbol: 'C$', locale: 'en-CA', name: 'Canadian Dollar' },
        AUD: { symbol: 'A$', locale: 'en-AU', name: 'Australian Dollar' }
      },

      // Timezone configuration
      timezoneConfig: {
        'UTC': { name: 'UTC', offset: '+00:00' },
        'America/New_York': { name: 'Eastern Time', offset: '-05:00' },
        'America/Los_Angeles': { name: 'Pacific Time', offset: '-08:00' },
        'Europe/London': { name: 'London', offset: '+00:00' },
        'Asia/Kolkata': { name: 'India', offset: '+05:30' }
      },

      // Set currency and update related properties
      setCurrency: (newCurrency) => {
        const config = get().currencyConfig[newCurrency];
        if (config) {
          set({
            currency: newCurrency,
            currencySymbol: config.symbol,
            currencyLocale: config.locale
          });
        }
      },

      // Set timezone
      setTimezone: (newTimezone) => {
        set({ timezone: newTimezone });
      },

      // Set date format
      setDateFormat: (newDateFormat) => {
        set({ dateFormat: newDateFormat });
      },

      // Format currency amount
      formatCurrency: (amount, options = {}) => {
        const { currency, currencyLocale } = get();
        const {
          minimumFractionDigits = 0,
          maximumFractionDigits = 2,
          showSymbol = true
        } = options;

        if (amount === null || amount === undefined || isNaN(amount)) {
          return showSymbol ? `${get().currencySymbol}0` : '0';
        }

        try {
          const formatter = new Intl.NumberFormat(currencyLocale, {
            style: showSymbol ? 'currency' : 'decimal',
            currency: currency,
            minimumFractionDigits,
            maximumFractionDigits
          });
          
          return formatter.format(amount);
        } catch (error) {
          // Fallback formatting
          const symbol = showSymbol ? get().currencySymbol : '';
          return `${symbol}${amount.toLocaleString()}`;
        }
      },

      // Format date according to user preference
      formatDate: (date, options = {}) => {
        const { dateFormat, timezone } = get();
        const { includeTime = false } = options;
        
        if (!date) return '';
        
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';

        try {
          let formatStr = dateFormat;
          
          // Convert format to toLocaleDateString options
          let formatOptions = {};
          
          switch (dateFormat) {
            case 'MM/DD/YYYY':
              formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
              break;
            case 'DD/MM/YYYY':
              formatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
              break;
            case 'YYYY-MM-DD':
              formatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
              break;
            default:
              formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
          }

          if (includeTime) {
            formatOptions.hour = '2-digit';
            formatOptions.minute = '2-digit';
            formatOptions.timeZone = timezone;
          }

          return dateObj.toLocaleDateString('en-US', formatOptions);
        } catch (error) {
          // Fallback to ISO string
          return dateObj.toLocaleDateString();
        }
      },

      // Format time according to timezone
      formatTime: (date) => {
        const { timezone } = get();
        
        if (!date) return '';
        
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';

        try {
          return dateObj.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone
          });
        } catch (error) {
          return dateObj.toLocaleTimeString();
        }
      },

      // Fetch settings from server
      fetchSettings: async () => {
        try {
          const response = await apiService.get('/admin/tenant/settings');
          const settings = response.data.data;
          
          if (settings.preferences) {
            const { currency, timezone, dateFormat } = settings.preferences;
            
            if (currency) get().setCurrency(currency);
            if (timezone) get().setTimezone(timezone);
            if (dateFormat) get().setDateFormat(dateFormat);
          }
          
          return { success: true };
        } catch (error) {
          console.error('Failed to fetch settings:', error);
          return { success: false, error: error.message };
        }
      },

      // Update settings on server
      updateSettings: async (newSettings) => {
        try {
          const currentSettings = await apiService.get('/admin/tenant/settings');
          const existingSettings = currentSettings.data.data;
          
          // Merge with existing settings
          const updatedSettings = {
            ...existingSettings,
            preferences: {
              ...existingSettings.preferences,
              ...newSettings
            }
          };
          
          await apiService.put('/admin/tenant/settings', updatedSettings);
          
          // Update local state
          if (newSettings.currency) get().setCurrency(newSettings.currency);
          if (newSettings.timezone) get().setTimezone(newSettings.timezone);
          if (newSettings.dateFormat) get().setDateFormat(newSettings.dateFormat);
          
          return { success: true };
        } catch (error) {
          console.error('Failed to update settings:', error);
          return { success: false, error: error.message };
        }
      }
    }),
    {
      name: 'system-settings',
      partialize: (state) => ({
        currency: state.currency,
        currencySymbol: state.currencySymbol,
        currencyLocale: state.currencyLocale,
        timezone: state.timezone,
        dateFormat: state.dateFormat
      })
    }
  )
);

export default useSettingsStore;
